import {AfterViewInit, Component, Inject, OnInit, QueryList, ViewChild, ViewChildren, ChangeDetectionStrategy} from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent, MatDialogRef,
  MatDialogTitle
} from "@angular/material/dialog";
import {MatButton, MatIconButton} from "@angular/material/button";
import {MatIcon} from "@angular/material/icon";
import {MatFormField, MatInput, MatLabel} from "@angular/material/input";
import {MatOption, MatSelect} from "@angular/material/select";
import {FormControl, ReactiveFormsModule, Validators} from "@angular/forms";
import {PipelinesConditionBlockComponent} from "./pipelines-condition-block/pipelines-condition-block.component";
import {PipelinesSortBlockComponent} from "./pipelines-sort-block/pipelines-sort-block.component";
import {PipelineFilterConditionType, PipelinesFilterService} from "./pipelines-filter.service";
import PipelineViewFilter from "../../../interfaces/pipeline-view-filter";
import PipelineFieldFilter from "../../../interfaces/pipeline-field-filter";
import {PipelinesService} from "../../pipelines.service";
import {MatButtonToggle, MatButtonToggleGroup} from "@angular/material/button-toggle";
import Group from "../../../interfaces/group";
import {Person} from "../../../interfaces/person";
import {MatSnackBar, MatSnackBarConfig} from "@angular/material/snack-bar";
import {map} from "rxjs/operators";
import {FundService} from "../../../funds/fund-service.service";
import {debounceTime, distinctUntilChanged} from "rxjs";
import Sponsor from "../../../interfaces/sponsor";
import Organization from "../../../interfaces/organization";
import UserPipelinePerm from "../../../interfaces/user-pipeline-permission";

@Component({
  selector: 'pipelines-filter-modal',
  standalone: false,
  templateUrl: './pipelines-filter-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './pipelines-filter-modal.component.scss',
})

export class PipelinesFilterModalComponent implements OnInit, AfterViewInit{
  @ViewChildren(PipelinesConditionBlockComponent) conditionBlocks!: QueryList<PipelinesConditionBlockComponent>;
  @ViewChild(PipelinesSortBlockComponent) sortBlock!: PipelinesSortBlockComponent;

  public showFilterBlocks:boolean = false;
  public baseViewFormControl: FormControl = new FormControl(null, Validators.required)
  public investorControl: FormControl = new FormControl(null)
  public pipelineNameFormControl: FormControl = new FormControl(null)
  public groupingControl: FormControl = new FormControl(null,Validators.required)
  public jsonGroupControl: FormControl = new FormControl()
  public hasJsonGroup: boolean = false;
  public searchResults:PipelineViewFilter[] = []
  public groupings:PipelineFieldFilter[] = []
  public jsonGroupings: any[] = []
  public conditionBlockNum: number[] = [0]
  public toggleControls: FormControl[] = []
  public pipelineViewTypeId: string;
  public user: Person | Group | null = null
  public userProfile: any = null;
  public isEditModal: boolean = false;
  public hasPermissionEdit: boolean = false;
  public isOwner: boolean = false;
  public canLinkInvestor: boolean = false;
  public investorResults: {sponsors: Sponsor[], orgs: Organization[]} = {sponsors: [], orgs: []}
  public isGlobal:boolean = false;
  private _pendingEdit: { blocks: any[], connectors: string[], sort: any[] } | null = null;


  constructor(
    public pipelineFilterService: PipelinesFilterService,
    public fundService: FundService,
    public pipelineService: PipelinesService,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<PipelinesFilterModalComponent>,
    private _snackBar: MatSnackBar
  ) {

  }

  ngOnInit() {

    this.baseViewFormControl.valueChanges.subscribe((changeResult:any) => {
      if(changeResult && changeResult.length!==0){
        this.showFilterBlocks = true;
      }
    })
    this.pipelineService.currentUser$.subscribe((result) => {
      this.userProfile = result.user_profile
      this.user = result.user_profile.user.person
      this.hasPermissionEdit = this._checkPermissions(result.user_profile.user)
      let hasSyndicationsGroup = result.user_profile.user.groupsFlattened.find((group:Group) => group.name.toLowerCase()==='syndications')
      if(hasSyndicationsGroup){
        this.canLinkInvestor = true;
      }
    })
    this.pipelineService.ownedViewFilters$.subscribe((result:any[]) => this.pipelineViewTypeId = result[0].pipelineViewType.id)
    this.pipelineFilterService.getGroupings().subscribe((filterGroups:any[]) => this.groupings = filterGroups)

    if((this.data && this.data.pipeline && !this.data.pipeline.hasOwnProperty('pipeline_view_filter_id')
      || (this.data && this.data.pipeline && this.data.pipeline.hasOwnProperty('pipeline_view_filter_id') && this.data.pipeline.pipeline_view_filter_id!==null))){
      this.isEditModal = true;
      let pipelineData:any = null

      this.pipelineService.combinedViewFilters$.pipe(
        map((filters) => filters.find((pd) => pd.id === this.data.pipeline.pipeline_view_filter_id))
      ).subscribe((result) => {
        pipelineData = result
      })

      const typeName =
        pipelineData?.pipelineViewType?.name ??
        this.data.pipeline?.pipelineViewType?.name;
      this.isOwner = this._checkOwner(this.userProfile, pipelineData)

      this.isGlobal = !typeName || typeName === 'global';

      this._formatPipelineForEdit(pipelineData ?? this.data.pipeline)
    } else {
      let defaultGroup = this.groupings.find((grouping: any) => grouping.name.toLowerCase()==='status')
      this.groupingControl.setValue(defaultGroup)
    }
    this.pipelineNameFormControl.valueChanges.subscribe((search:string) => {
      this.pipelineFilterService.searchFilterByName(search).subscribe((filtered:PipelineViewFilter[]) => this.searchResults = filtered.slice(0, 5))
    })


    this.investorControl.valueChanges.pipe(
      debounceTime(500),
      distinctUntilChanged()
    ).subscribe((value:string) => {
      if(value!==null && value!==''){
        this.pipelineFilterService.searchInvestorOrganizations(value).subscribe({
          next: (result:any) => {
            this.investorResults.sponsors = result.data.search_sponsors.data;

            this.investorResults.orgs =
              result.data.search_organizations.data.filter((og: Organization) =>
                og.entity?.entity_types?.some(
                  (et: any) => et.type.name.toLowerCase() === 'investor'
                )
              );

          }
        })
      } else {
        this.investorResults.sponsors = []
        this.investorResults.orgs = []
      }
    })
  }


  public compareGroups(a: any, b: any): boolean {
    const aId = typeof a === 'string' ? a : a?.id;
    const bId = typeof b === 'string' ? b : b?.id;
    return String(aId) === String(bId);
  }


  ngAfterViewInit(){
    if(this._pendingEdit){
      this.conditionBlocks.changes.subscribe(() => this._applyPendingEdit());
      this._applyPendingEdit();
    }
  }

  private _applyPendingEdit(){
    if (!this._pendingEdit) { return; }
    const blocks = this.conditionBlocks.toArray();
    if (blocks.length < this._pendingEdit.blocks.length) {
      // Render more blocks, then changes subscription re-runs this.
      this.addConditionBlock();
      return;
    }

    this.pipelineService.fieldFilters$.subscribe((fields: any[]) => {
      this._pendingEdit!.blocks.forEach((b, i) => {
          blocks[i]?.resolveFromSaved(b.value, fields, b.filter_type)
          blocks[i]?.syncConditionToggle()
        }
      );
    });
    this._pendingEdit.connectors.forEach((c, i) => {
      if (this.toggleControls[i]) {
        this.toggleControls[i].setValue(c.toLowerCase());
      }
    });
    if (this.sortBlock && this._pendingEdit.sort.length) {
      this.pipelineService.sortFilters$.subscribe((sortFields: any[]) =>
        this.sortBlock.resolveFromSaved(this._pendingEdit!.sort, sortFields));
    }
    this._pendingEdit = null;
  }

  public parse(field:string){
    return this.pipelineService.parseField(field)
  }


  private _checkPermissions(user: any): boolean {
    let groupIds: string[] = user.groupsFlattened.map((grp: any) => grp.id);
    let pipelineGroup = this.data.pipeline?.person ?? this.data.pipeline.group

    if(pipelineGroup && pipelineGroup?.person?.id===user.id) return true;

    return user.permissionPipelines.some((perm: UserPipelinePerm) => {
      let pipelineId = this.data.pipeline.pipeline_view_filter_id || this.data.pipeline.id
      if(!pipelineId || perm.pipeline.id!==pipelineId) return false;
      if (!perm._edit) return false;

      const acls = perm.pipeline.acls;

      return acls.some((acl: any) =>
        groupIds.includes(acl.acc.group?.id || acl.acc.user?.id)
      );
    });
  }

  public _checkOwner(user_profile:any, data:any){
    let groupIds:string[] = user_profile.user.groupsFlattened.map((grp:any) => grp.id)
    groupIds.push(user_profile.user.person.id)
    let pipelineId = data?.person_id ?? data?.group_id
    return groupIds.includes(pipelineId)
  }



  public viewPipeline(){
    let formattedData = this.pipelineFilterService.formatForViewPipeline(this.conditionBlocks.toArray(), this.sortBlock, {
      group: this.groupingControl,
      jsonGroup: this.jsonGroupControl,
      toggleControls: this.toggleControls
    })
    const obj:any = {
      filter: formattedData['data'],
      sort: formattedData['sort'],
      group: formattedData['group'],
      pipeline_view_id: this.baseViewFormControl.value.id
    }
    this.pipelineService.getFilteredPipeline(obj).subscribe({
      next: () => this.dialogRef.close(),
      error: () => console.warn('Error on load.')
    })
  }

  public addConditionBlock(){
    this.conditionBlockNum.push(this.conditionBlockNum[this.conditionBlockNum.length-1] + 1)
    this.toggleControls.push(new FormControl(PipelineFilterConditionType.AND))
  }

  public removeConditionBlock(index:number){
    this.conditionBlockNum = this.conditionBlockNum.filter((num: number, i:number) => i !== index)
  }
  public clearFilterData(){
    this.showFilterBlocks = false;
    this.conditionBlockNum = [0]
    this.baseViewFormControl.reset()
    this.pipelineNameFormControl.reset()
    this.groupingControl.reset()
    this.jsonGroupControl.reset()
    // this.conditionBlockNum = []
    this.data = null;
    this.isEditModal = false;
  }

  public onGroupChange(event:any, isSecondaryControl:boolean = false){
    if(event.value.format=='json'){
      this.hasJsonGroup = true;
      let childrenGroup = event.value.children.filter((child: PipelineFieldFilter) => child.is_grouped)
      this.pipelineService.getModelLookup(childrenGroup[0].model, childrenGroup[0].field, true)
      this.pipelineService.groupingSelections$.subscribe((result) => this.jsonGroupings = result)
    } else {
      this.hasJsonGroup = isSecondaryControl
    }
  }

  public savePipelineFilter(saveAs:boolean = false){
    let formattedData = this.pipelineFilterService.formatForViewPipeline(this.conditionBlocks.toArray(), this.sortBlock, {
      group: this.groupingControl,
      jsonGroup: this.jsonGroupControl,
      toggleControls: this.toggleControls
    })
    if(this.isEditModal && !this.isGlobal && !saveAs){
      const obj:any = {
        name: this.pipelineNameFormControl.value,
        id: this.data.pipeline.pipeline_view_filter_id ?? this.data.pipeline.id,
        sort: formattedData['sort'],
        group: formattedData['group'],
        filter: formattedData['data'],
        sponsor_id: this.investorControl.value?.__typename=='Sponsor' ? this.investorControl.value.id : null,
        organization_id: this.investorControl.value?.__typename=='Organization' ? this.investorControl.value.id : null,
        pipeline_view_id: this.baseViewFormControl.value.id,
        person_id: this.user?.id
      }
      this.pipelineService.updatePipelineFilter(obj).subscribe({
        next: () =>{
          this._openSnackBar('save-success')
          this.pipelineService.getPipelineViews()
          this.viewPipeline()
        },
        error: () => this._openSnackBar('save-fail')
      })
    } else {
      const obj:any = {
        name: this.pipelineNameFormControl.value,
        pipeline_view_type_id: this.pipelineViewTypeId,
        filter: formattedData['data'],
        sort: formattedData['sort'],
        group: formattedData['group'],
        pipeline_view_id: this.baseViewFormControl.value.id,
        sponsor_id: this.investorControl.value?.__typename=='Sponsor' ? this.investorControl.value.id : null,
        organization_id: this.investorControl.value?.__typename=='Organization' ? this.investorControl.value.id : null,
        person_id: this.user?.id
      }
      this.pipelineService.savePipelineFilter(obj).subscribe({
        next: () => {
          this._openSnackBar('save-success')
          this.pipelineService.getPipelineViews()
          this.viewPipeline()
        },
        error: () => this._openSnackBar('save-fail')
      })
    }
  }

  private _formatPipelineForEdit(pipeline: PipelineViewFilter){
    this.baseViewFormControl.setValue(pipeline.pipelineView)
    this.pipelineNameFormControl.setValue(pipeline.name)

    if(pipeline.associatedSponsor!==null){
      this.investorControl.setValue(pipeline.associatedSponsor)
    }
    if(pipeline.associatedOrganization!==null){
      this.investorControl.setValue(pipeline.associatedOrganization)
    }

    let group = pipeline.group !== null ? typeof pipeline.group==='string' ? this.pipelineService.parseField(pipeline.group) : pipeline.group : null
    let sort = pipeline.sort !== null ? typeof pipeline.sort==='string' ? this.pipelineService.parseField(pipeline.sort) : pipeline.sort : null
    let filter = pipeline.filter !== null ? typeof pipeline.filter==='string'? this.pipelineService.parseField(pipeline.filter) : pipeline.filter : null
    let normalizedSort = Array.isArray(sort) ? sort : []

    let setGroup = group ? this.groupings.find((gp: any) => gp.field == group[0].field) : null

    this.groupingControl.setValue(setGroup ?? null)
    if (setGroup) {
      this.onGroupChange({ value: setGroup })
      this.jsonGroupControl.setValue(group[0]?.group?.[0]?.group)
    }

    this.pipelineService.baseViews$.subscribe((views: any[]) => {
      const match = views.find((v: any) => v.id === pipeline.pipeline_view_id)
      if (match) { this.baseViewFormControl.setValue(match) }
    })

    const { blocks, connectors } = filter
      ? this.pipelineFilterService.extractBlocks(filter)
      : { blocks: [], connectors: [] }

    this.conditionBlockNum = blocks.map((bl:any, index:number) => index)
    for (let i = 1; i < this.conditionBlockNum.length; i++) {
      this.toggleControls.push(new FormControl(PipelineFilterConditionType.AND))
    }
    this.showFilterBlocks = true;

    this._pendingEdit = { blocks, connectors, sort: normalizedSort }
  }

  public displayFn(option:any) {
    return option?.name || ''
  }

  private _openSnackBar(configClass:string){
    const config = new MatSnackBarConfig();
    config.panelClass = [configClass];
    config.horizontalPosition = "center";
    config.verticalPosition = "top";
    config.duration = 3000;
    let message = configClass == 'save-fail' ? 'There was an error saving the pipeline filters.' : 'Pipeline filter successfully saved.'
    this._snackBar.open(message, 'Ok', config)
  }

  protected readonly PipelineFilterConditionType = PipelineFilterConditionType;
}
